/**
 * Block 12 — типы столешниц (5 канонических значений per методике).
 *
 * Применимо к 2 анкетам со столешницей:
 *   - **Кухня** (`type='kitchen'`) — все 5 опций. ЛДСП имеет дополнительный
 *     параметр `countertopSheetLen` ∈ `{3000, 4000}` мм (user picks 3 или 4 м).
 *     HPL — фикс лист 4 м (ADR-0033, без выбора).
 *   - **Тумба-ванна** (`type='bath-tabletop'`) — все 5 опций. PR-A1 (ADR-0033):
 *     ЛДСП — user picks 3/4 м (как Кухня), HPL — фикс лист 4 м.
 *
 * **Камень** (акрил/кварц/литой) — qty в **погонных метрах** (decimal), деплинк
 * «Подрядчики». **ЛДСП/HPL** — qty в **листах** (integer ceil), деплинк
 * «Подбери декор».
 *
 * ## Naming disambiguation
 *
 * ⚠️ B2 frozen formula module (`calculator/formulas/countertopQ.ts:23`) уже
 * экспортирует `CountertopKind` как **English internal dispatch enum**:
 * `'laminated' | 'hpl' | 'acrylic-stone' | 'quartz-stone' | 'cast-stone'` —
 * используется как ключ в `countertopUnit(kind)` и в Kitchen/BathTabletop
 * formula dispatchers. Этот тип сохраняется (B2 frozen, ТЗ §4).
 *
 * Чтобы избежать `export *` collision в `packages/shared/src/index.ts`, новый
 * **Russian-string vocabulary type** называется **`CountertopKindLabel`** (вместо
 * `CountertopKind` из Phase B3 ТЗ §A.3). Это **canonical user-facing value**,
 * используемый в `z.enum(COUNTERTOP_KINDS)` Zod-схемах и в UI PickerSheet.
 *
 * Mapping Russian ↔ English делается в Part C `helpers/countertopKindMap.ts`:
 * `'Столешницы ЛДСП' → 'laminated'`, `'Камень акрил' → 'acrylic-stone'`, etc.
 *
 * Consumers, которые типизируют `params.countertopKind`, получают Russian-union
 * через Zod inference (`z.infer<typeof KitchenParamsSchema>['countertopKind']`)
 * или импортом `CountertopKindLabel` explicit'но.
 *
 * Reference:
 *   - `docs/methodology/calculator-formulas.md` §2.1 Б.6 (Кухня), §2.3 Б.6 (Тумба-ванна),
 *     §4.9 cheat-sheet (countertopQ formulas per kind).
 *   - Aibek канон xlsx 14.05.2026 +: 5 типов столешниц, sheetLen ∈ {3000, 4000} только Кухня ЛДСП.
 *   - Phase B3 ТЗ §A.3 (Commit #2 spec) — naming изменён из-за B2 collision.
 *   - B2 frozen formula type: `calculator/formulas/countertopQ.ts:23`.
 */

export const COUNTERTOP_KINDS = [
  'Столешницы ЛДСП',
  'Столешницы HPL',
  'Камень акрил',
  'Камень кварц',
  'Литой камень',
] as const;

export type CountertopKindLabel = (typeof COUNTERTOP_KINDS)[number];

/** Камень — qty в погонных метрах (decimal). ЛДСП/HPL — qty в листах (integer ceil). */
export const STONE_COUNTERTOP_KINDS = ['Камень акрил', 'Камень кварц', 'Литой камень'] as const;
export type StoneCountertopKindLabel = (typeof STONE_COUNTERTOP_KINDS)[number];

/** Type guard: `kind` — каменная столешница (qty в пог. метрах). */
export function isStoneCountertop(kind: CountertopKindLabel): kind is StoneCountertopKindLabel {
  return (STONE_COUNTERTOP_KINDS as readonly string[]).includes(kind);
}

/**
 * Длина листа столешницы ЛДСП — user picks 3 или 4 м (per OQ-3 ответ Aibek).
 *
 * PR-A1 (ADR-0033): применяется для ЛДСП в **обеих** анкетах (Кухня +
 * Тумба-ванна). HPL — фикс 4000 (не выбирается). Параметр Zod-схемы
 * `countertopSheetLen` required только при `countertopKind='Столешницы ЛДСП'`
 * (refinement в `kitchen`/`bath-tabletop` схемах).
 */
export const COUNTERTOP_SHEET_LEN_OPTIONS = [3000, 4000] as const;
export type CountertopSheetLen = (typeof COUNTERTOP_SHEET_LEN_OPTIONS)[number];
